import React, { useState } from 'react'
import Layout from '../Layout'
import ResourceForm from '../../../Components/ResourceForm'
import FormFieldController from '../../../Components/FormFieldController'
import BackLink from '../../../Components/BackLink'
import CurrentImagePreview from '../../../Components/CurrentImagePreview'
import { venueSchema } from '../../../schemas'

function CheckboxSection({ title, items, selectedIds, onChange, labelKey = 'name' }) {
  const toggle = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter(i => i !== id)
      : [...selectedIds, id]
    onChange(next)
  }

  return (
    <div className="bg-white shadow rounded-lg mt-6 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
      {items.length === 0 ? (
        <p className="text-gray-500 text-sm">None available</p>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <label key={item.id} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIds.includes(item.id)}
                onChange={() => toggle(item.id)}
                className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
              />
              <span className="text-gray-900">{item[labelKey]}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

function BlockedDatesSection({ dates, onUpdate }) {
  const [newDate, setNewDate] = useState('')

  const addDate = () => {
    if (newDate && !dates.includes(newDate)) {
      onUpdate([...dates, newDate].sort())
      setNewDate('')
    }
  }

  const removeDate = (date) => {
    onUpdate(dates.filter(d => d !== date))
  }

  return (
    <div className="bg-white shadow rounded-lg mt-6 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Blocked Dates</h2>
      <p className="text-sm text-gray-500 mb-4">
        Dates when this venue is not available for booking.
      </p>

      <div className="flex gap-2 mb-4">
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          className="rounded border-gray-300 text-sm"
          min={new Date().toISOString().split('T')[0]}
        />
        <button
          type="button"
          onClick={addDate}
          disabled={!newDate}
          className="px-3 py-2 bg-sky-600 text-white text-sm rounded hover:bg-sky-700 disabled:opacity-50"
        >
          Block Date
        </button>
      </div>

      {dates.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {dates.map(date => (
            <span
              key={date}
              className="inline-flex items-center gap-1 bg-red-50 text-red-700 px-3 py-1 rounded-full text-sm"
            >
              {date}
              <button
                type="button"
                onClick={() => removeDate(date)}
                className="text-red-400 hover:text-red-600"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Edit({
  account,
  accounts,
  navigation,
  venue,
  packages = [],
  capabilities = []
}) {
  const [packageIds, setPackageIds] = useState(venue.package_ids || [])
  const [capabilityIds, setCapabilityIds] = useState(venue.capability_ids || [])
  const [blockedDates, setBlockedDates] = useState(venue.blocked_dates || [])

  return (
    <Layout
      account={account}
      accounts={accounts}
      navigation={navigation}
      title={`Edit: ${venue.name}`}
    >
      <BackLink href="/manage/venues">Back to Venues</BackLink>

      <div className="bg-white shadow rounded-lg">
        <ResourceForm
          schema={venueSchema}
          resource="venue"
          endpoint={`/manage/venues/${venue.id}`}
          method="patch"
          backLink="/manage/venues"
          submitLabel="Save Changes"
          defaultValues={{
            name: venue.name || '',
            description: venue.description || '',
            capacity: venue.capacity || '',
            min_booking_notice_hours: venue.min_booking_notice_hours ?? 24,
            setup_time_minutes: venue.setup_time_minutes ?? 0,
            base_hire_fee_pence: venue.base_hire_fee_pence ?? '',
            active: venue.active ?? true,
            image: null
          }}
          extraData={{
            package_ids: packageIds,
            capability_ids: capabilityIds,
            blocked_dates: blockedDates
          }}
        >
          {({ control, errors }) => (
            <>
              <FormFieldController
                control={control}
                name="name"
                label="Name"
                required
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="description"
                label="Description"
                type="textarea"
                rows={3}
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="capacity"
                label="Capacity"
                type="number"
                errors={errors}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormFieldController
                  control={control}
                  name="min_booking_notice_hours"
                  label="Min. Notice (hours)"
                  type="number"
                  errors={errors}
                />

                <FormFieldController
                  control={control}
                  name="setup_time_minutes"
                  label="Setup Time (minutes)"
                  type="number"
                  errors={errors}
                />
              </div>

              <FormFieldController
                control={control}
                name="base_hire_fee_pence"
                label="Base Hire Fee (pence)"
                type="number"
                errors={errors}
                placeholder="e.g., 50000 for £500"
                hint="Optional. Leave blank if venue hire is included in packages."
              />

              <CurrentImagePreview
                url={venue.image_url}
                alt={venue.name}
              />

              <FormFieldController
                control={control}
                name="image"
                label={venue.image_url ? "Replace Image" : "Image"}
                type="file"
                accept="image/*"
                hint={venue.image_url ? "Leave empty to keep current image" : undefined}
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="active"
                label="Active"
                type="checkbox"
                hint="Uncheck to hide this venue from guests"
                errors={errors}
              />
            </>
          )}
        </ResourceForm>
      </div>

      <CheckboxSection
        title="Associated Packages"
        items={packages}
        selectedIds={packageIds}
        onChange={setPackageIds}
      />

      <CheckboxSection
        title="Amenities"
        items={capabilities}
        selectedIds={capabilityIds}
        onChange={setCapabilityIds}
      />

      <BlockedDatesSection
        dates={blockedDates}
        onUpdate={setBlockedDates}
      />
    </Layout>
  )
}
